import React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertCircleIcon } from 'lucide-react';
import { useDatabase } from '../hooks/useDatabase';
import { usePublicForm } from '../hooks/usePublicContent';
import { submitLead } from '../services/leadService';
import { readOrigin } from '../utils/utm';
import { isValidUrl, postSubmitConfig } from '../utils/postSubmit';
import { DynamicForm, DynamicFormResult } from '../components/public/DynamicForm';
import { Button } from '../components/ui/Button';
import { BrandLogo } from '../components/shared/BrandLogo';

/** Formulario público independiente: /formulario/:slug */
export function PublicForm() {
  const {
    slug = ''
  } = useParams();
  const db = useDatabase();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    status,
    message,
    retry
  } = usePublicForm(slug);
  const form = db.forms.find(f => f.slug === slug);
  const organization = form ? db.organizations.find(o => o.id === form.organizationId) : undefined;
  const logoUrl = organization?.brand.logoUrl ?? organization?.logoUrl;
  if (status === 'cargando' && !form) {
    return <main className="flex min-h-screen w-full items-center justify-center bg-surface" aria-busy="true" data-id="element-1825" data-mp="3lyec1xe3mpbg">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary" data-id="element-1826" data-mp="3bxvpuvz2awqd" />
        <span className="sr-only" data-id="element-1827" data-mp="dfyrvtzzzihg">Cargando el formulario…</span>
      </main>;
  }
  if (status === 'error' && !form) {
    return <main className="flex min-h-screen w-full flex-col items-center justify-center gap-4 bg-background px-6 text-center" data-id="element-1828" data-mp="8gvmu9k0pc9h">
        <AlertCircleIcon className="h-10 w-10 text-muted-foreground" aria-hidden="true" data-id="element-1829" data-mp="3oudave8hzn5" />
        <h1 className="text-2xl font-semibold text-navy" data-id="element-1830" data-mp="3uvyhzjmwp9se">
          No hemos podido cargar el formulario
        </h1>
        <p className="max-w-md text-sm text-muted-foreground" data-id="element-1831" data-mp="w810ci2hjsgm">
          Comprueba tu conexión e inténtalo de nuevo.
          {message ? ` (${message})` : ''}
        </p>
        <Button onClick={retry} data-id="element-1832" data-mp="2lowjqa38g26r">Reintentar</Button>
      </main>;
  }
  if (!form) {
    return <main className="flex min-h-screen w-full flex-col items-center justify-center gap-4 bg-background px-6 text-center" data-id="element-1833" data-mp="2qn7t6jpm8aog">
        <AlertCircleIcon className="h-10 w-10 text-muted-foreground" data-id="element-1834" data-mp="2bx8vsj5vx3a1" />
        <h1 className="text-2xl font-semibold text-navy" data-id="element-1835" data-mp="2gwd6xfcddb30">
          Formulario no disponible
        </h1>
        <Link to="/" data-id="element-1836" data-mp="22x40h7dytvc8">
          <Button variant="secondary" data-id="element-1837" data-mp="22vsnqi2jld8z">Volver al inicio</Button>
        </Link>
      </main>;
  }
  async function handleSubmit(result: DynamicFormResult) {
    if (!form) return;
    await submitLead({
      organizationId: form.organizationId,
      formId: form.id,
      contact: result.contact,
      answers: result.answers,
      origin: readOrigin(location.search, {
        formId: form.id
      })
    });
    // El lead ya está guardado; ahora se ejecuta la acción configurada.
    const action = postSubmitConfig(form);
    if (action.action === 'url' && isValidUrl(action.redirectUrl)) {
      window.location.href = action.redirectUrl!.trim();
      return;
    }
    // WhatsApp y mensaje simple se resuelven dentro del propio formulario.
    if (action.action === 'whatsapp' || action.action === 'mensaje') return;
    navigate(`/gracias/${form.thankYouSlug ?? form.slug}`);
  }
  return <main className="min-h-screen w-full bg-surface px-5 py-16 sm:py-24" data-id="element-1838" data-mp="1ln9ntdeuc3xx">
      <div className="mx-auto mb-12 max-w-2xl text-center" data-id="element-1839" data-mp="1zen48ihgcloq">
        <div className="mb-10 flex justify-center" data-id="element-1840" data-mp="1zh9bsi4cf16y">
          <BrandLogo src={logoUrl} size={30} data-id="element-1841" data-mp="1ufbmb65cna5k" />
        </div>
        <h1 className="text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-1842" data-mp="29dm53evz7d16">
          {form.name}
        </h1>
        {form.description && <p className="mt-4 text-base text-muted-foreground sm:text-lg" data-id="element-1843" data-mp="24fub8fbrtguc">
            {form.description}
          </p>}
      </div>
      <DynamicForm form={form} onSubmit={handleSubmit} data-id="element-1844" data-mp="f1p5uzral2vk" />
    </main>;
}